import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { Check, ArrowRight } from "lucide-react";
import { services, images } from "../mock";
import SectionHeading from "../components/SectionHeading";
import CtaBand from "../components/CtaBand";
import TechBackdrop from "../components/TechBackdrop";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";

const heroImages = [images.serverArray, images.cables, images.rackMounted, images.serverMgmt, images.monitor, images.serversRack];

export default function ServicesPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    }
  }, [hash]);

  return (
    <>
      <section className="relative pt-36 lg:pt-44 pb-12 overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <TechBackdrop variant="hex" opacity={0.06} className="hidden lg:block" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <SectionHeading
            eyebrow="Services"
            title="Specialist IBM Power, AIX & infrastructure consulting."
            description="Every service below is delivered by engineers with 15+ years on the platform. Scoped tightly, costed transparently, documented meticulously."
          />
        </div>
      </section>

      <section className="pb-4">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {services.map((s) => {
              const Icon = Icons[s.icon] || Icons.Server;
              return (
                <a key={s.id} href={`#${s.id}`} className="group rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3 hover:border-brand-500 hover:bg-brand-50/30 transition-colors">
                  <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 grid place-items-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900 text-[14px]">{s.title}</div>
                    <div className="text-[12px] text-slate-500">{s.short}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-brand-700 group-hover:translate-x-0.5 transition-all" />
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {services.map((s, idx) => {
        const Icon = Icons[s.icon] || Icons.Server;
        const reverse = idx % 2 === 1;
        return (
          <section id={s.id} key={s.id} className="py-20 scroll-mt-24">
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
              <div className={`grid lg:grid-cols-12 gap-10 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div className="lg:col-span-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-50 border border-brand-100 text-brand-700 text-[12px] font-medium">
                    <Icon className="h-3.5 w-3.5" /> 0{idx + 1} · {s.title}
                  </div>
                  <h2 className="font-display mt-4 text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
                    {s.short}
                  </h2>
                  <p className="mt-4 text-[16px] leading-relaxed text-slate-600">{s.description}</p>
                  <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                    {s.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-[14px] text-slate-700">
                        <Check className="h-4 w-4 text-brand-600 mt-0.5 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7 flex gap-3">
                    <Link to="/contact">
                      <Button className="rounded-full h-11 bg-brand-700 hover:bg-brand-800 text-white px-5">
                        Scope a {s.title} project <ArrowRight className="ml-1.5 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
                <div className="lg:col-span-6">
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-[28px] bg-gradient-to-br from-brand-400/30 via-transparent to-transparent blur-xl" />
                    <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl">
                      <img src={heroImages[idx % heroImages.length]} alt={s.title} className="w-full h-[400px] object-cover" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <CtaBand />
    </>
  );
}
